import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, X, Plus, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { absoluteMediaUrl } from "@/components/ImageField";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

export function ShopNewsPanel() {
  const [products, setProducts] = useState([]);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState("");

  const load = async () => {
    try {
      const [p, s] = await Promise.all([api.get("/admin/products"), api.get("/settings")]);
      setProducts(p.data);
      setF({
        shop_news_enabled: !!s.data.shop_news_enabled,
        shop_news_title: s.data.shop_news_title || "Novità dallo Shop",
        shop_news_intro: s.data.shop_news_intro || "",
        shop_news_ids: s.data.shop_news_ids || [],
      });
    } catch { toast.error("Errore caricamento"); }
  };
  useEffect(() => { load(); }, []);

  if (!f) return null;

  const selected = f.shop_news_ids.map((id) => products.find((p) => p.id === id)).filter(Boolean);
  const available = products.filter((p) => p.published !== false && !f.shop_news_ids.includes(p.id));

  const add = () => {
    if (!pick) return;
    setF({ ...f, shop_news_ids: [...f.shop_news_ids, pick] });
    setPick("");
  };
  const remove = (id) => setF({ ...f, shop_news_ids: f.shop_news_ids.filter((x) => x !== id) });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= f.shop_news_ids.length) return;
    const arr = f.shop_news_ids.slice();
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setF({ ...f, shop_news_ids: arr });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", f);
      toast.success("Sezione Novità salvata");
    } catch { toast.error("Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";

  return (
    <section className="bg-white border border-hair p-5 md:p-8" data-testid="settings-section-shop-news">
      <div className="eyebrow">Novità Shop in homepage</div>
      <label className="mt-4 flex items-center gap-3 text-sm cursor-pointer min-h-[44px] max-w-md">
        <Switch checked={f.shop_news_enabled} onCheckedChange={(v) => setF({ ...f, shop_news_enabled: v })} data-testid="shop-news-enabled" />
        <span>Sezione attiva<span className="block text-xs text-taupe">Se nessun prodotto è selezionato la sezione resta comunque nascosta.</span></span>
      </label>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Titolo sezione</Label>
          <Input value={f.shop_news_title} onChange={(e) => setF({ ...f, shop_news_title: e.target.value })} className="rounded-none mt-1" data-testid="shop-news-title" />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Testo introduttivo</Label>
          <Textarea rows={2} value={f.shop_news_intro} onChange={(e) => setF({ ...f, shop_news_intro: e.target.value })} className="rounded-none mt-1" data-testid="shop-news-intro" />
        </div>
      </div>

      <div className="mt-5">
        <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Prodotti in evidenza (solo pubblicati)</Label>
        <div className="flex gap-2 mt-2">
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger className="rounded-none" data-testid="shop-news-pick"><SelectValue placeholder="Scegli un prodotto…" /></SelectTrigger>
            <SelectContent>
              {available.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {eur(p.price)}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={add} disabled={!pick} className={`${btn} bg-ink text-bone hover:bg-gold shrink-0`} data-testid="shop-news-add"><Plus size={14} /> Aggiungi</button>
        </div>
        <div className="mt-3 space-y-2">
          {selected.map((p, i) => (
            <div key={p.id} className="flex items-center gap-3 border border-hair p-2.5" data-testid={`shop-news-item-${p.id}`}>
              <img src={absoluteMediaUrl(p.image)} alt="" className="w-12 h-12 object-cover border border-hair" />
              <span className="flex-1 text-sm truncate">{p.name}</span>
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 hover:text-gold disabled:opacity-30"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} disabled={i === selected.length - 1} className="p-2 hover:text-gold disabled:opacity-30"><ArrowDown size={14} /></button>
              <button onClick={() => remove(p.id)} className="p-2 hover:text-red-700" data-testid={`shop-news-remove-${p.id}`}><X size={14} /></button>
            </div>
          ))}
          {selected.length === 0 && <p className="text-xs text-taupe py-2">Nessuna novità selezionata.</p>}
        </div>
      </div>
      <button onClick={save} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold mt-5`} data-testid="shop-news-save"><Save size={14} /> Salva Novità</button>
    </section>
  );
}
